import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ErrorAlert from './ErrorAlert';
import LoadingSpinner from './LoadingSpinner';

export type SortDir = 'asc' | 'desc';
type Breakpoint = 'sm' | 'md' | 'lg';

export interface DataTableColumn<T> {
  /** Stable column id (used for sort state + React keys). */
  id: string;
  /** Header label. */
  header: string;
  /** Cell renderer. */
  render: (row: T) => React.ReactNode;
  /** Provide to make the column sortable; returns the comparable value. */
  sortValue?: (row: T) => string | number | boolean | null | undefined;
  /** Provide to include this column's text in the global search. */
  searchValue?: (row: T) => string;
  align?: 'left' | 'right';
  /** Hide the column below this breakpoint (responsive). */
  hideBelow?: Breakpoint;
  /** Mark the actions column so its header label is visually hidden. */
  actions?: boolean;
  width?: number | string;
}

export interface DataTableFilter<T> {
  id: string;
  label: string;
  options: { value: string; label: string }[];
  /** Client-mode predicate. `value` is never the sentinel "all". */
  match: (row: T, value: string) => boolean;
}

export interface DataTableProps<T> {
  rows: T[];
  columns: DataTableColumn<T>[];
  getRowId: (row: T) => string;

  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  emptyMessage?: React.ReactNode;

  /** Toolbar. */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Declarative client-mode filters rendered as selects in the toolbar. */
  filters?: DataTableFilter<T>[];
  /** Extra toolbar controls (e.g. custom date-range filters), left side. */
  extraFilters?: React.ReactNode;
  /** Toolbar actions (create / export), right side. */
  toolbarActions?: React.ReactNode;

  defaultSort?: { columnId: string; dir: SortDir };
  paginate?: boolean;
  rowsPerPageOptions?: number[];
  defaultRowsPerPage?: number;

  onRowClick?: (row: T) => void;
  /** Optional expandable detail row. */
  renderExpanded?: (row: T) => React.ReactNode;
  isRowExpanded?: (row: T) => boolean;

  ariaLabel: string;
  /** Hide the internal toolbar entirely (e.g. when the page renders its own). */
  hideToolbar?: boolean;
}

const cmp = (a: unknown, b: unknown): number => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

const hideSx = (bp?: Breakpoint) =>
  bp ? { display: { xs: 'none', [bp]: 'table-cell' } } : undefined;

/**
 * Reusable, accessible data table with global search, column sorting, declarative
 * filters and pagination — all client-side over the supplied `rows`. Pages keep
 * loading their own data and pass it in; columns declare how to render / sort /
 * search each field. See StationLibraryList for a typical usage.
 */
function DataTable<T>({
  rows,
  columns,
  getRowId,
  loading,
  error,
  onRetry,
  emptyMessage,
  searchable,
  searchPlaceholder,
  filters,
  extraFilters,
  toolbarActions,
  defaultSort,
  paginate = true,
  rowsPerPageOptions = [10, 25, 50, 100],
  defaultRowsPerPage = 25,
  onRowClick,
  renderExpanded,
  isRowExpanded,
  ariaLabel,
  hideToolbar,
}: DataTableProps<T>) {
  const { t } = useTranslation('common');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ columnId: string; dir: SortDir } | null>(defaultSort ?? null);
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

  const searchableCols = useMemo(() => columns.filter((c) => c.searchValue), [columns]);
  const showSearch = (searchable ?? searchableCols.length > 0) && searchableCols.length > 0;

  const filtered = useMemo(() => {
    let out = rows;
    const q = search.trim().toLowerCase();
    if (q && searchableCols.length > 0) {
      out = out.filter((row) =>
        searchableCols.some((c) => c.searchValue!(row).toLowerCase().includes(q)),
      );
    }
    for (const f of filters ?? []) {
      const v = filterValues[f.id];
      if (v && v !== 'all') out = out.filter((row) => f.match(row, v));
    }
    return out;
  }, [rows, search, searchableCols, filters, filterValues]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const col = columns.find((c) => c.id === sort.columnId);
    if (!col?.sortValue) return filtered;
    const dir = sort.dir === 'asc' ? 1 : -1;
    // copy before sort so we never mutate the parent's array
    return [...filtered].sort((a, b) => dir * cmp(col.sortValue!(a), col.sortValue!(b)));
  }, [filtered, sort, columns]);

  const total = sorted.length;
  // Keep the page in range when the filtered set shrinks.
  const maxPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
  const safePage = Math.min(page, maxPage);
  const visibleRows = paginate
    ? sorted.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage)
    : sorted;

  const toggleSort = (col: DataTableColumn<T>) => {
    if (!col.sortValue) return;
    setSort((prev) =>
      prev?.columnId === col.id
        ? { columnId: col.id, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { columnId: col.id, dir: 'asc' },
    );
  };

  if (loading) return <LoadingSpinner />;

  const toolbar = hideToolbar ? null : (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1.5}
      sx={{ mb: 2, alignItems: { xs: 'stretch', md: 'center' }, flexWrap: 'wrap' }}
    >
      {showSearch && (
        <TextField
          label={t('table.search')}
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          size="small"
          sx={{ minWidth: 220 }}
        />
      )}
      {(filters ?? []).map((f) => (
        <FormControl key={f.id} size="small" sx={{ minWidth: 160 }}>
          <InputLabel id={`dt-filter-${f.id}`}>{f.label}</InputLabel>
          <Select
            labelId={`dt-filter-${f.id}`}
            label={f.label}
            value={filterValues[f.id] ?? 'all'}
            onChange={(e) => {
              setFilterValues((prev) => ({ ...prev, [f.id]: e.target.value }));
              setPage(0);
            }}
          >
            <MenuItem value="all">{t('table.allFilter')}</MenuItem>
            {f.options.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ))}
      {extraFilters}
      {toolbarActions && <Box sx={{ flexGrow: 1 }} />}
      {toolbarActions}
    </Stack>
  );

  return (
    <Box>
      {toolbar}
      {error && <ErrorAlert message={error} onRetry={onRetry} />}
      {!error && total === 0 ? (
        <Paper variant="outlined" sx={{ py: 6, textAlign: 'center' }}>
          {typeof emptyMessage === 'string' || emptyMessage == null ? (
            <Typography variant="body1" color="text.secondary">
              {emptyMessage ?? t('table.noResults')}
            </Typography>
          ) : (
            emptyMessage
          )}
        </Paper>
      ) : (
        !error && (
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label={ariaLabel}>
              <TableHead>
                <TableRow>
                  {columns.map((col) => {
                    const active = sort?.columnId === col.id;
                    return (
                      <TableCell
                        key={col.id}
                        scope="col"
                        align={col.align}
                        sortDirection={active ? sort!.dir : false}
                        sx={{ width: col.width, ...hideSx(col.hideBelow) }}
                      >
                        {col.sortValue ? (
                          <TableSortLabel
                            active={active}
                            direction={active ? sort!.dir : 'asc'}
                            onClick={() => toggleSort(col)}
                          >
                            {col.actions ? (
                              <Box component="span" sx={{ position: 'absolute', overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                                {col.header}
                              </Box>
                            ) : (
                              col.header
                            )}
                          </TableSortLabel>
                        ) : col.actions ? (
                          <Box component="span" sx={{ position: 'absolute', overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                            {col.header}
                          </Box>
                        ) : (
                          col.header
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((row) => {
                  const expanded = isRowExpanded?.(row) ?? false;
                  return (
                    <React.Fragment key={getRowId(row)}>
                      <TableRow
                        hover
                        onClick={onRowClick ? () => onRowClick(row) : undefined}
                        sx={onRowClick ? { cursor: 'pointer' } : undefined}
                      >
                        {columns.map((col) => (
                          <TableCell key={col.id} align={col.align} sx={hideSx(col.hideBelow)}>
                            {col.render(row)}
                          </TableCell>
                        ))}
                      </TableRow>
                      {renderExpanded && expanded && (
                        <TableRow>
                          <TableCell colSpan={columns.length} sx={{ py: 0 }}>
                            {renderExpanded(row)}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
            {paginate && (
              <Stack
                direction="row"
                spacing={2}
                sx={{ alignItems: 'center', justifyContent: 'flex-end', px: 2, py: 1 }}
              >
                <Typography variant="caption" color="text.secondary">
                  {t('table.rowsPerPage')}
                </Typography>
                <Select
                  size="small"
                  variant="standard"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(0);
                  }}
                  inputProps={{ 'aria-label': t('table.rowsPerPage') }}
                  sx={{ minWidth: 56 }}
                >
                  {rowsPerPageOptions.map((n) => (
                    <MenuItem key={n} value={n}>
                      {n}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary">
                  {t('table.range', {
                    from: total === 0 ? 0 : safePage * rowsPerPage + 1,
                    to: Math.min((safePage + 1) * rowsPerPage, total),
                    total,
                  })}
                </Typography>
                <IconButton
                  size="small"
                  aria-label={t('pagination.previous')}
                  disabled={safePage <= 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label={t('pagination.next')}
                  disabled={safePage >= maxPage}
                  onClick={() => setPage((p) => Math.min(maxPage, p + 1))}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Stack>
            )}
          </TableContainer>
        )
      )}
    </Box>
  );
}

export default DataTable;
